import React, { useRef, useMemo } from "react";
import { Modal } from "@mui/material";
import { useForm } from "react-hook-form";
import { XIcon, UploadIcon, DocumentTextIcon } from "@heroicons/react/solid";
import { Button, Input, DateInput, CreatableSelect } from "../../components/ui";
import { usePettyCashForm } from "../../hooks/petty_cash/usePettyCashForm";
import { usePettyCashSettings } from "../../hooks/petty_cash/usePettyCashSettings";
import InlineLoader from "../../components/loader/InlineLoader";
import { getFileType } from "../../utility";
import Zoom from "react-medium-image-zoom";
import "react-medium-image-zoom/dist/styles.css";

const PettyCashForm = ({ open, onClose, onSuccess, companyEmployeeId }) => {
  const { settingsData } = usePettyCashSettings();
  const settings = settingsData.data?.data;
  const isSettingsLoading = settingsData.isLoading;
  const priorNoDays = settings && !Array.isArray(settings) ? parseInt(settings.prior_no_days, 10) : 0;

  const minAllowedDate = useMemo(() => {
    const d = new Date();
    d.setDate(d.getDate() - priorNoDays);
    d.setHours(0, 0, 0, 0);
    return d;
  }, [priorNoDays]);

  const parseDate = (value) => {
    if (!value) return null;
    if (value instanceof Date) return value;
    return new Date(value);
  };

  const dateLimitValidate = (value) => {
    if (!value || !priorNoDays) return true;
    const date = parseDate(value);
    if (!date) return true;
    return date >= minAllowedDate || `Transaction date cannot be earlier than ${priorNoDays} day${priorNoDays > 1 ? "s" : ""} prior`;
  };
  const fileInputRef = useRef(null);
  const { control, handleSubmit, reset, watch } = useForm({
    defaultValues: {
      transaction_type: "DR",
      expenses_date: new Date(),
      particulars: "",
      expenses_type_id: "",
      amount: "",
      invoice_no: "",
      chq_no: "",
      chq_date: null,
      additional_remarks: "",
    },
  });

  const transactionType = watch("transaction_type");

  const {
    expenseTypes,
    mediaId,
    mediaPath,
    isUploading,
    isSaving,
    handleFileChange,
    removeReceipt,
    handleSave,
  } = usePettyCashForm({
    companyEmployeeId,
    onSuccess,
    onClose,
    reset,
  });

  const isFormLocked = isSaving || isUploading || isSettingsLoading;

  const handleFormClose = () => {
    if (!isFormLocked) {
      onClose();
    }
  };

  const onSubmit = (data) => {
    handleSave(data);
  };

  return (
    <>
      <Modal
        open={open}
        onClose={handleFormClose}
        disableEscapeKeyDown={isFormLocked}
        onBackdropClick={!isFormLocked ? handleFormClose : undefined}
        aria-labelledby="petty-cash-form-title"
      >
        <div className="w-full h-full md:h-auto md:w-[90%] lg:w-[80%] xl:w-[70%] max-w-5xl bg-white rounded-none md:rounded-xl shadow-2xl absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 overflow-hidden border-0 md:border md:border-gray-100 flex flex-col">
          <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 flex-shrink-0">
            <h3 id="petty-cash-form-title" className="text-xl font-bold text-gray-800">
              Add Transaction
            </h3>
            <button
              onClick={handleFormClose}
              disabled={isFormLocked}
              className="p-1.5 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-200 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <XIcon className="w-5 h-5" />
            </button>
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-5 flex-1 overflow-y-auto md:max-h-[80vh] flex flex-col">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-8 gap-y-5">
              <div className="space-y-5">
                {/* Toggle Type */}
                <div className="flex flex-col space-y-2">
                  <span className="text-sm font-semibold text-gray-700">Transaction Type</span>
                  <div className="grid grid-cols-2 gap-2 bg-gray-100 p-1 rounded-xl">
                    <button
                      type="button"
                      onClick={() => reset((prev) => ({ ...prev, transaction_type: "DR" }))}
                      className={`py-2 text-sm font-semibold rounded-lg transition-all ${transactionType === "DR"
                        ? "bg-red-600 text-white shadow-md"
                        : "text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                      Expense (Debit)
                    </button>
                    <button
                      type="button"
                      onClick={() => reset((prev) => ({ ...prev, transaction_type: "CR" }))}
                      className={`py-2 text-sm font-semibold rounded-lg transition-all ${transactionType === "CR"
                        ? "bg-green-600 text-white shadow-md"
                        : "text-gray-600 hover:bg-gray-200"
                        }`}
                    >
                      Income (Credit)
                    </button>
                  </div>
                </div>

                {priorNoDays > 0 && (
                  <div className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-xl px-3 py-2">
                    Transaction date cannot be earlier than {priorNoDays} day{priorNoDays > 1 ? "s" : ""} prior.
                  </div>
                )}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <DateInput
                    control={control}
                    label="Transaction Date"
                    name="expenses_date"
                    minDate={minAllowedDate}
                    rules={{
                      required: "Date is required",
                      validate: dateLimitValidate,
                    }}
                  />
                  <Input
                    control={control}
                    label="Amount"
                    name="amount"
                    type="number"
                    isCurrency={true}
                    placeholder="0.00"
                    rules={{
                      required: "Amount is required",
                      min: { value: 0.01, message: "Amount must be greater than 0" },
                    }}
                  />
                </div>

                <CreatableSelect
                  control={control}
                  label="Category"
                  name="expenses_type_id"
                  rules={{ required: "Category is required" }}
                  options={expenseTypes.data?.data || []}
                  customKey={{ value: "id", label: "name" }}
                />
                <Input
                  control={control}
                  label="Particulars"
                  name="particulars"
                  placeholder="Enter particulars"
                  rules={{ required: "Particulars are required" }}
                />

                <Input
                  control={control}
                  label="Invoice/Voucher No"
                  name="invoice_no"
                  placeholder="Enter invoice or voucher number"
                />

                {/* Cheque Details (Optional) */}
                <div className="border-t border-gray-100 pt-4">
                  <h4 className="text-sm font-semibold text-gray-500 mb-3">Cheque Details (Optional)</h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Input
                      control={control}
                      label="Cheque Number"
                      name="chq_no"
                      placeholder="Enter cheque number"
                    />
                    <DateInput
                      control={control}
                      label="Cheque Date"
                      name="chq_date"
                    />
                  </div>
                </div>

                {/* Additional Remarks */}
                <Input
                  control={control}
                  label="Additional Remarks"
                  name="additional_remarks"
                  placeholder="Add any additional remarks here..."
                  isTextarea={true}
                />
              </div>

              {/* Right Column - Dedicated Receipt / Supporting Document Preview */}
              <div className="flex flex-col h-full space-y-3">
                <span className="text-sm font-semibold text-gray-700">
                  Receipt / Supporting Document (Optional)
                </span>
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={(e) => {
                    handleFileChange(e);
                  }}
                  accept="image/*,application/pdf"
                  className="hidden"
                />

                <div className="flex-1 flex flex-col justify-between">
                  {mediaPath ? (
                    <div className="relative group border border-gray-200 rounded-xl bg-gray-50 overflow-hidden transition-all duration-300 hover:shadow-md flex flex-col h-full justify-between">
                      {getFileType(mediaPath) === "pdf" ? (
                        <div className="relative flex flex-col flex-1">
                          <div className="p-2 bg-gray-100/50 flex items-center justify-center flex-1 min-h-[380px]">
                            <iframe
                              src={`${mediaPath}#toolbar=0&navpanes=0`}
                              title="Receipt PDF"
                              className="w-full h-[400px] rounded-lg border border-gray-200 bg-white shadow-inner"
                            />
                          </div>
                          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-100">
                            <div className="flex items-center space-x-2">
                              <DocumentTextIcon className="w-5 h-5 text-red-500 flex-shrink-0" />
                              <span className="text-xs font-semibold text-gray-500">
                                PDF Attachment
                              </span>
                            </div>
                            <div className="flex items-center space-x-3">
                              <a
                                href={mediaPath}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs font-semibold text-blue-600 hover:text-blue-800 transition-colors"
                              >
                                Open Fullscreen
                              </a>
                              <button
                                type="button"
                                onClick={() => removeReceipt(fileInputRef)}
                                className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors flex items-center space-x-1"
                              >
                                <XIcon className="w-4 h-4 inline" />
                                <span>Remove</span>
                              </button>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <div className="relative flex flex-col flex-1">
                          <div
                            className="relative flex items-center justify-center bg-gray-100/50 p-4 flex-1 min-h-[380px]"
                          >
                            <Zoom>
                              <img
                                src={mediaPath}
                                alt="Receipt Supporting Document"
                                className="max-h-[400px] w-full object-contain rounded-lg shadow-sm transition-transform duration-300 group-hover/image:scale-[1.01] cursor-zoom-in"
                              />
                            </Zoom>
                          </div>
                          <div className="flex items-center justify-between px-4 py-3 bg-white border-t border-gray-100">
                            <span className="text-xs font-semibold text-gray-500">
                              Receipt Image
                            </span>
                            <button
                              type="button"
                              onClick={() => removeReceipt(fileInputRef)}
                              className="text-xs font-semibold text-red-500 hover:text-red-700 transition-colors flex items-center space-x-1"
                            >
                              <XIcon className="w-4 h-4 inline" />
                              <span>Remove</span>
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={isUploading}
                      className="flex flex-col items-center justify-center border-2 border-dashed rounded-xl hover:bg-gray-50 transition-all w-full min-h-[460px] py-12 border-gray-300 hover:border-gray-400"
                    >
                      {isUploading ? (
                        <>
                          <InlineLoader className="mb-2" />
                          <span className="text-sm text-gray-500">Uploading receipt...</span>
                        </>
                      ) : (
                        <>
                          <UploadIcon className="w-10 h-10 text-gray-400 mb-3" />
                          <span className="text-sm font-semibold text-gray-600">Select receipt file</span>
                          <span className="text-xs text-gray-400 mt-1">Image or PDF</span>
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>

            <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-100">
              <Button
                type="button"
                variant="outline"
                onClick={onClose}
                disabled={isFormLocked}
              >
                {isSettingsLoading ? "Loading settings..." : "Cancel"}
              </Button>
              <Button
                type="submit"
                disabled={isFormLocked}
                className={transactionType === "DR" ? "bg-red-600 hover:bg-red-700" : "bg-green-600 hover:bg-green-700"}
              >
                {isSettingsLoading ? (
                  <>
                    <span>Loading settings...</span>
                    <InlineLoader className="ml-2" />
                  </>
                ) : isSaving ? (
                  <>
                    <span>Saving...</span>
                    <InlineLoader className="ml-2" />
                  </>
                ) : (
                  "Save Transaction"
                )}
              </Button>
            </div>
          </form>
        </div>
      </Modal>
    </>
  );
};

export default PettyCashForm;
